import moment from 'moment'

/**
 * Formats a deadline into a human-readable countdown string.
 *
 * | Remaining Time | Display                          | isWarning |
 * |----------------|----------------------------------|-----------|
 * | Expired        | "Expired"                        | true      |
 * | > 24h          | "Closes on 21 Feb, 3:30 PM"      | false     |
 * | 24h → 1h       | "Closes in 3h 45m"               | false     |
 * | < 1h           | "Closes in 12m 34s"              | true      |
 *
 * @param {Date|string|null} deadlineDate - The deadline timestamp
 * @returns {{ text: string, isWarning: boolean, isExpired: boolean } | null}
 *   Returns null if deadlineDate is falsy or invalid.
 */
export function formatDeadlineCountdown(deadlineDate) {
    if (!deadlineDate) return null

    const deadline = moment(deadlineDate)
    if (!deadline.isValid()) return null

    const now = moment()
    const diffMs = deadline.diff(now)

    // Expired
    if (diffMs <= 0) {
        return { text: 'Expired', isWarning: true, isExpired: true }
    }

    const totalSeconds = Math.floor(diffMs / 1000)
    const totalMinutes = Math.floor(totalSeconds / 60)
    const totalHours = Math.floor(totalMinutes / 60)

    // > 24 hours → show formatted expiry date
    if (totalHours >= 24) {
        return {
            text: `Closes on ${deadline.format('DD MMM, h:mm A')}`,
            isWarning: false,
            isExpired: false,
        }
    }

    // 1h – 24h → show hours and minutes
    if (totalMinutes >= 60) {
        const hours = Math.floor(totalMinutes / 60)
        const mins = totalMinutes % 60

        return {
            text: `Closes in ${hours}h ${mins}m`,
            isWarning: true,
            isExpired: false,
        }
    }

    // < 1h → show minutes and seconds (warning)
    const mins = Math.floor(totalSeconds / 60)
    const secs = totalSeconds % 60

    return {
        text: `Closes in ${mins}m ${secs}s`,
        isWarning: true,
        isExpired: false,
    }
}